import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "@xplatform/design-system";
import { IconLock } from "@tabler/icons-react";
import { usePermissions } from "@hooks/usePermissions";

interface PermissionGuardProps {
  /** The permission the user must hold to see `children`. */
  require: "access" | "review" | "admin";
  children: ReactNode;
  /** Shown instead of `children` when the permission is missing. */
  fallback?: ReactNode;
}

/**
 * Gates `children` on a generation tier, applying the D21 hierarchy (admin
 * implies review implies access). When the user lacks it, renders a
 * design-system `EmptyState` explaining the missing access rather than the
 * content. Used to gate the module (`access`) and the review routes (`review`);
 * the Publish and Regenerate controls gate on `admin` inline, not through this
 * guard.
 */
export function PermissionGuard({
  require,
  children,
  fallback,
}: PermissionGuardProps) {
  const { t } = useTranslation();
  const { canAccess, canReview, canAdmin } = usePermissions();

  const allowed =
    require === "access"
      ? canAccess
      : require === "review"
        ? canReview
        : canAdmin;

  if (allowed) return <>{children}</>;

  return (
    <>
      {fallback ?? (
        <EmptyState
          icon={<IconLock size={48} aria-hidden="true" />}
          title={t("review.noPermission.title")}
          description={t("review.noPermission.description")}
        />
      )}
    </>
  );
}
